import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from '../auth/tokenStorage';

const API_BASE =
  (typeof __RCATS_API_URL__ !== 'undefined' && __RCATS_API_URL__) ||
  import.meta.env.VITE_API_URL ||
  'http://localhost:3001/v1';

export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function silentRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const { data } = await axios.post<{ accessToken: string; refreshToken?: string }>(
      `${API_BASE}/auth/refresh`,
      { refreshToken: refresh },
    );
    setAccessToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    return data.accessToken;
  } catch {
    clearTokens();
    return null;
  }
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      if (!refreshPromise) {
        refreshPromise = silentRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
      window.dispatchEvent(new CustomEvent('rcats:auth-expired'));
    }
    return Promise.reject(error);
  },
);

export function getApiBaseUrl(): string {
  return API_BASE;
}

/** Origin without `/v1` — used for absolute media URLs. */
export function getApiOrigin(): string {
  return API_BASE.replace(/\/v1\/?$/, '');
}

/**
 * Resolve avatar/media paths from the API.
 * Supports `/v1/media/...` (current) and legacy `/media/...`.
 */
export function resolveMediaUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const origin = getApiOrigin().replace(/\/$/, '');
  let normalized = path.startsWith('/') ? path : `/${path}`;
  // Legacy DB values pointed at StaticFiles `/media/...` which is unreliable;
  // prefer the API-routed `/v1/media/...` path that img tags can load.
  if (normalized.startsWith('/media/')) {
    normalized = `/v1${normalized}`;
  }
  return `${origin}${normalized}`;
}
